import queryString from 'query-string';
import type { MouseEvent } from 'react';
import { useCallback } from 'react';
import { useLocation } from 'react-router';

import { useUser } from '#/contexts/UserEnvironmentContext';
import { useDeferredNavigate } from '#/hooks/useDeferredNavigate';
import { useI18n } from '#/i18n/hooks/useI18n';
import {
  useSearchActions,
  useSearchGetters,
  useSearchLocation,
} from '#/modules/search/contexts/SearchContext';
import { getParsedSearchQuery } from '#/modules/search/helpers/getParsedSearchQuery';
import type { SearchPageLocationState } from '#/modules/search/pages/Search/types/SearchPageLocationState';
import { searchGetRoutePathSwitch } from '#/modules/search/routing/helpers/searchGetRoutePathSwitch';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';
import type { SearchType } from '#/modules/search/types/SearchType';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { EMPTY_SEARCH_LOCATION } from '#/utils/constants/general/emptySearchLocation';
import { deferWithBriefLoadingAnimation } from '#/utils/ui/routeLoadingAnimation';
import { showModal } from '#/zustand-stores/modalStore';

type Args = {
  searchFrom: string | undefined;
};

export function useSearchHero({ searchFrom }: Args) {
  const { locale } = useI18n();
  const location = useLocation();
  const navigate = useDeferredNavigate();

  const { user } = useUser();

  const { getSearchLocation } = useSearchGetters();

  const { updateSearchLocation } = useSearchActions();

  const searchLocation = useSearchLocation();

  const initialQuery = '';

  const changeSearchLocation = (
    newSearchLocation: SearchLocation | undefined,
  ): boolean => {
    if (newSearchLocation) {
      // The LocationInput triggers the change event twice except for the `Remote` option
      if (
        newSearchLocation.text &&
        getSearchLocation()?.text === newSearchLocation.text
      )
        return false;

      deferWithBriefLoadingAnimation(() => {
        updateSearchLocation(newSearchLocation, true);
      });
    } else {
      updateSearchLocation(EMPTY_SEARCH_LOCATION, true);
    }

    return true;
  };

  const search = useCallback(
    (data: {
      query: string;
      listingType: SearchType;
      triggeredByRemoteLocationChange: boolean;
    }) => {
      const { parsedQuery, locationType, remoteKeywords } =
        getParsedSearchQuery({ query: data.query, locale });

      const path = searchGetRoutePathSwitch({
        locale,
        q: parsedQuery,
        filtersByName: {
          type: data.listingType,
          locationType: data.triggeredByRemoteLocationChange
            ? ['REMOTE']
            : locationType,
        },
        embedSearchWidgetId: null,
      });

      navigate(path, {
        state: { searchFrom, remoteKeywords } satisfies SearchPageLocationState,
      });
    },
    [locale, navigate, searchFrom],
  );

  const redirectTo = location ? `${location.pathname}${location.search}` : '';
  const redirectParam = queryString.stringify({ to: redirectTo });

  const clickLogin = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    trackClicked('Log In Search Hero');
    showModal('LOGIN', { redirectTo });
  };

  const clickSignup = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    trackClicked('Sign Up Search Hero');
    showModal('SIGNUP', { redirectTo });
  };

  return {
    initialQuery,
    redirectParam,
    search,
    searchLocation,
    changeSearchLocation,
    clickLogin,
    clickSignup,
    showLogin: !user,
  };
}
